import { onIdTokenChanged } from "firebase/auth";

import { auth } from "./firebase.utils";
import { isAnalyticsAllowed } from "./consent";

/**
 * First-party product events, and the GA4 copy of the ones GA may see
 * (behaviour-logging TR §2.4, §11).
 *
 *   track(eventName, props = {}, gaContext = {})
 *
 * One call, two sinks:
 *
 * - The first-party log: queued in memory, sent to POST /api/v1/events in
 *   batches — every 10 s, at 20 events, and by beacon when the tab is hidden
 *   or closed. `props` must match the event's model in
 *   packages/api/events_schemas.py exactly: undeclared keys are rejected
 *   server-side, never stored.
 * - GA4: only events in GA_EVENTS, only their allow-listed parameters (picked
 *   from `props`, a few renamed fields, and `gaContext` — GA-only context such
 *   as a state code, which the first-party schema has no field for), plus
 *   `zp_session_id`. Anything not on the list is dropped, so a new property
 *   can never reach Google by accident.
 *
 * Consent: necessary-tier events (sign-in, errors, exports) are always logged
 * first-party; every other event is dropped — not queued — without analytics
 * consent, and GA never gets anything without it. Never throws: telemetry
 * must not break the flow that reports it.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const EVENTS_URL = `${API_BASE_URL}/api/v1/events`;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH = 20; // comfortably under the server's 50-event cap
const SESSION_KEY = "zatoplan_session_id";

// Mirrors NECESSARY_EVENTS in packages/api/events_schemas.py. Duplicated, not
// imported: if either list changes, change the other and diff the two by hand
// (TR §2.4, task 6.22).
export const NECESSARY_EVENTS = new Set([
  "sign_in",
  "sign_up",
  "error_surfaced",
  "report_exported",
]);

/**
 * The only parameters that may leave for Google (TR §11.2). `page_view` is not
 * here on purpose — useGaPageViews sends the scrubbed GA page view — and
 * neither is `error_surfaced` (a message can echo what someone typed).
 */
export const GA_EVENTS = {
  address_searched: ["entry_point", "region"],
  result_viewed: ["verdict_band", "score_type", "pwsid", "region"],
  contaminant_detail_opened: ["contaminant", "verdict_band"],
  all_contaminants_opened: [],
  map_layer_toggled: ["layer", "enabled"],
  nearby_system_clicked: ["pwsid"],
  compare_used: ["count"],
  property_saved: ["verdict_band"],
  property_removed: ["verdict_band"],
  report_exported: ["format", "verdict_band"],
  sign_in: ["method", "feature_id"],
  sign_up: ["method", "feature_id"],
  sign_in_prompted: ["feature_id", "trigger"],
  sign_in_dismissed: ["feature_id", "reason"],
  gated_action_resumed: ["feature_id", "outcome"],
};

/** GA4's recommended names, where they differ from ours. */
export const GA_NAME = { sign_in: "login" };

const ENTRY_POINT = {
  marketing_hero: "hero",
  console_search: "console",
  app_search: "result_zero_state",
};

/** First-party fields that §11.2 names differently for GA. */
const renamed = (props) => ({
  entry_point: ENTRY_POINT[props.surface],
  contaminant: props.contaminant_code,
  count: props.property_count,
});

const pick = (source, keys) => {
  const out = {};
  keys.forEach((k) => {
    const v = source[k];
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") out[k] = v;
  });
  return out;
};

// --- session id (FRS FR7) ---------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  const b = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

// Storage can be missing or throw (blocked cookies, private modes): every
// access is guarded, and the id falls back to living in memory for the tab.
const store = (name) => {
  try {
    return window[name];
  } catch {
    return null;
  }
};
const readId = (s) => {
  try {
    const v = s?.getItem(SESSION_KEY);
    return v && UUID_RE.test(v) ? v : null;
  } catch {
    return null;
  }
};
const writeId = (s, v) => {
  try {
    s?.setItem(SESSION_KEY, v);
  } catch {
    // storage unavailable
  }
};

let memoryId = null;

/**
 * This tab's session id, created on first call. Synchronous, so the console's
 * lookups can send it on the first request of a page load (R4).
 *
 * Always in sessionStorage (tab-scoped). Mirrored to localStorage — and read
 * from there first in a new tab — only with analytics consent, so without it
 * the id dies with the tab.
 */
export const getSessionId = () => {
  const persist = isAnalyticsAllowed();
  const id =
    readId(store("sessionStorage")) ||
    memoryId ||
    (persist ? readId(store("localStorage")) : null) ||
    uuid();
  memoryId = id;
  writeId(store("sessionStorage"), id);
  if (persist) writeId(store("localStorage"), id);
  return id;
};

// --- signed-in token (FRS FR8) ----------------------------------------------------

let lastToken = null;
let watchingToken = false;

// The beacon cannot wait for getIdToken(), so the latest token is cached from
// Firebase as it changes. Started on first use rather than at import: this
// module and firebase.utils import each other.
const watchToken = () => {
  if (watchingToken) return;
  watchingToken = true;
  try {
    onIdTokenChanged(auth, async (user) => {
      try {
        lastToken = user ? await user.getIdToken() : null;
      } catch {
        lastToken = null;
      }
    });
  } catch {
    // no auth in this environment: events go anonymous
  }
};

const freshToken = async () => {
  try {
    const user = auth?.currentUser;
    if (!user) return null;
    lastToken = await user.getIdToken();
    return lastToken;
  } catch {
    return lastToken;
  }
};

// --- queue and flush --------------------------------------------------------------

let queue = []; // [{ necessary, event }]
let timer = null;

const post = async (body) => {
  if (typeof fetch !== "function") return;
  const headers = { "Content-Type": "application/json" };
  const token = await freshToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  // Fire-and-forget: a lost batch is acceptable, an error in the caller's flow
  // is not.
  fetch(EVENTS_URL, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    keepalive: true,
    credentials: "omit",
  }).catch(() => {});
};

const send = (events, useBeacon) => {
  const sessionId = getSessionId();
  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    // A plain string goes out as text/plain, a CORS-safelisted type, so the
    // cross-origin beacon needs no preflight. It cannot carry a header, so a
    // signed-in user's token rides in the body (TR §4.2).
    const body = { session_id: sessionId, events };
    if (lastToken) body.id_token = lastToken;
    try {
      if (navigator.sendBeacon(EVENTS_URL, JSON.stringify(body))) return;
    } catch {
      // fall through to fetch
    }
  }
  post({ session_id: sessionId, events });
};

/** Send everything queued now. Exported for tests; the module flushes itself. */
export const flush = (useBeacon = false) => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  // Consent can be withdrawn between queueing and sending: analytics events
  // queued before that are discarded, necessary ones still go.
  if (!isAnalyticsAllowed()) queue = queue.filter((q) => q.necessary);
  while (queue.length) {
    send(
      queue.splice(0, MAX_BATCH).map((q) => q.event),
      useBeacon,
    );
  }
};

const schedule = () => {
  if (!timer) timer = setTimeout(() => flush(), FLUSH_INTERVAL_MS);
};

const sendToGa = (eventName, props, gaContext) => {
  const allowed = GA_EVENTS[eventName];
  if (!allowed || !isAnalyticsAllowed() || typeof window.gtag !== "function") return;
  const params = pick({ ...props, ...renamed(props), ...gaContext }, allowed);
  params.zp_session_id = getSessionId();
  window.gtag("event", GA_NAME[eventName] ?? eventName, params);
};

export const track = (eventName, props = {}, gaContext = {}) => {
  try {
    const necessary = NECESSARY_EVENTS.has(eventName);
    if (!necessary && !isAnalyticsAllowed()) return; // dropped, never queued
    watchToken();
    queue.push({
      necessary,
      event: {
        event_name: eventName,
        occurred_at: new Date().toISOString(),
        client_event_id: uuid(),
        props,
      },
    });
    try {
      sendToGa(eventName, props, gaContext);
    } catch {
      // GA is best-effort
    }
    if (queue.length >= MAX_BATCH) flush();
    else schedule();
  } catch {
    // never into the caller
  }
};

/**
 * `error_surfaced` (necessary tier, never sent to GA): the message truncated,
 * no stack, and the path without its query string — the address lives there.
 * Callers skip errors access tiers marks handled (a cancelled sign-in prompt
 * is the user's choice, not a failure).
 */
export const trackError = (message, httpStatus = null) =>
  track("error_surfaced", {
    message: String(message || "Unknown error").slice(0, 500),
    route: String(window.location.pathname || "/").slice(0, 200),
    http_status:
      Number.isInteger(httpStatus) && httpStatus >= 100 && httpStatus <= 599 ? httpStatus : null,
  });

/**
 * The cookie banner's save handler calls this with the new analytics choice.
 * Granted: the session id is persisted. Withdrawn: the persisted copy is
 * removed (the tab keeps its sessionStorage id for the rest of its life) and
 * queued analytics events are dropped.
 */
export const applyAnalyticsConsent = (allowed) => {
  if (allowed) {
    getSessionId();
    return;
  }
  try {
    store("localStorage")?.removeItem(SESSION_KEY);
  } catch {
    // storage unavailable
  }
  queue = queue.filter((q) => q.necessary);
};

if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
}

/** Tests only: forget module state. */
export const resetTrackForTests = () => {
  queue = [];
  if (timer) clearTimeout(timer);
  timer = null;
  memoryId = null;
  lastToken = null;
  watchingToken = false;
};

export default track;
